import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Plus, RefreshCw, Settings, Sparkles } from 'lucide-react';
import type {
  ModelProvider,
  ModelProviderSecret,
  ModelProviderSecretScope,
  ModelProviderSecretType,
} from '@reflex/shared';
import { MODEL_PROVIDER_LABELS, PersistableModelProviderSchema } from '@reflex/shared';
import { useAccessibleModelProviderSecrets } from '@reflex/ui/client/hooks';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { useHoverPreviewGroup } from '../ui/hover-preview-group';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '../ui/command';
import { Badge } from '../ui/badge';
import { OwnerChip } from '../ownership/OwnerChip';
import { ModelProviderSecretChip } from './ModelProviderSecretChip';
import { ModelProviderSecretPreviewCard } from './ModelProviderSecretPreviewCard';
import { AddModelProviderKeyForm } from './AddModelProviderKeyForm';
import { modelProviderSecretDisplayName } from './secret-display';

const SCOPE_GROUP_LABEL: Record<ModelProviderSecretScope, string> = {
  user: 'Your keys',
  team: 'Team keys',
  org: 'Organization keys',
};

// Most-specific first, matching the precedence the server resolves with.
const SCOPE_GROUP_ORDER: readonly ModelProviderSecretScope[] = ['user', 'team', 'org'];

/**
 * Caller-supplied affordance for launching the provider's subscription
 * registration flow (Claude Max, Codex, …) from inside the picker. When
 * supplied and the picker is showing subscription types, a footer button
 * surfaces it so the user can register a subscription without leaving
 * the surface they're on. The picker itself doesn't know how to register
 * a subscription — the caller owns the dialog mount and wiring.
 */
export interface ProviderSubscriptionAffordance {
  /** Footer button label, e.g. `Add Claude Max subscription…`. */
  label: string;
  /** Invoked on click. The picker closes its popover before invoking. */
  onAdd: () => void;
}

/**
 * Caller-supplied "re-apply the credential this is already on" affordance.
 * When supplied, the row for the currently-selected secret grows a trailing
 * refresh button. Picking the same row from the list is a no-op by design
 * (the value did not change), so this is the only way to ask a surface to
 * re-push a credential it is already pointed at — e.g. re-writing a running
 * agent's subscription onto its devbox after the stored token was renewed.
 * The picker only renders the control; the caller owns what refreshing means.
 */
export interface ProviderSecretRefreshAffordance {
  /** Accessible name and tooltip, e.g. `Refresh this subscription on the devbox`. */
  label: string;
  /** Invoked on click. The popover stays open so the pending state is visible. */
  onRefresh: () => void;
  /** True while a refresh is in flight: the button spins and stops accepting clicks. */
  pending?: boolean;
}

/**
 * Caller-supplied "update the credential behind this row" affordance. When
 * supplied, every subscription row the signed-in user owns grows a trailing cog
 * that hands the row's secret back to the caller — which opens the provider's
 * subscription dialog pinned to that credential. Subscriptions expire, so the
 * surface where a user notices a stale one (the picker) is where they should be
 * able to re-paste it, instead of navigating to the settings page and finding
 * the row again. The picker only renders the control; the caller owns the
 * dialog.
 */
export interface ProviderSecretManageAffordance {
  /** Accessible name and tooltip, e.g. `Update this subscription`. */
  label: string;
  /** Invoked with the row's secret. The picker closes its popover first. */
  onManage: (secret: ModelProviderSecret) => void;
}

export interface ModelProviderSecretPickerProps {
  /** Currently-selected `mps_*` id, or `null` when nothing is picked. */
  value: string | null;
  onChange: (id: string | null) => void;
  /** Strict filter — only secrets for this provider are offered. */
  provider: ModelProvider;
  /**
   * Secret types the surface accepts. Launch passes
   * `['apiKey', 'subscription']`; the flow-chat composer passes
   * `['apiKey']` since chat can only wire a raw bearer token.
   */
  allowedTypes: readonly ModelProviderSecretType[];
  /**
   * Secret types that are fetched/shown but NOT selectable for the current
   * provider — rendered greyed with a hint (e.g. subscription keys for a
   * provider that only supports API keys). Must be a subset of `allowedTypes`.
   * A secret whose type is listed here is never auto-selected and is cleared
   * if it was the current value.
   */
  disabledTypes?: readonly ModelProviderSecretType[];
  /**
   * When set, the last pick is remembered under this `localStorage` key
   * and pre-selected on mount if it still resolves to a candidate.
   */
  storageKey?: string;
  /**
   * Href to the page where keys are managed, shown when the list is empty.
   * Only used as a fallback: surfaces that offer inline add affordances
   * (`allowCreate` / `addSubscription`) surface those rows instead.
   */
  onEmptyAddHref?: string;
  /**
   * When true, the picker offers an inline "Add a key" affordance that
   * renders {@link AddModelProviderKeyForm} *inside* the popover (provider
   * pre-filled, owner = personal/team) so the user can create a key
   * without leaving the surface. The new key is selected automatically
   * on save. Falls back to `onEmptyAddHref` (a link to the management
   * page) when false.
   */
  allowCreate?: boolean;
  /**
   * Optional subscription registration affordance. When supplied and
   * `allowedTypes` includes `'subscription'` (and `disabledTypes` doesn't),
   * the popover footer surfaces a "{label}…" button that fires
   * `onAdd` — including when the user has no keys at all, which is when
   * they most need it. The caller owns the registration dialog
   * (subscriptions are paste-JSON flows too tall for the popover).
   */
  addSubscription?: ProviderSubscriptionAffordance;
  /**
   * Optional refresh affordance for the selected row. See
   * {@link ProviderSecretRefreshAffordance}. Ignored in the read-only
   * (`disabled` + `compact`) details view, which lists no rows.
   */
  refreshSelected?: ProviderSecretRefreshAffordance;
  /**
   * Optional "update this subscription" affordance. See
   * {@link ProviderSecretManageAffordance}. Ignored in the read-only
   * (`disabled` + `compact`) details view, which lists no rows.
   */
  manageSubscription?: ProviderSecretManageAffordance;
  disabled?: boolean;
  /**
   * Owner of the resource the key is being selected for (persona / flow /
   * flow node). Restricts the offered keys to the scopes that owner may
   * bind: `user` -> user+team+org, `team` -> team+org (only the owning
   * team), `organization` -> org only. Single prefixed id — kind is
   * derived from the id's prefix.
   */
  owner?: {
    ownerId: string;
  };
  /** Width utility for the trigger. Defaults to a fixed compact width. */
  className?: string;
  /**
   * Render the trigger with no fill or border (ghost button). Used next to
   * a matching model picker in the flow generate composer. Ignored when
   * `segment` is set, which is already ghost.
   */
  ghost?: boolean;
  /**
   * Render the trigger as a borderless bar segment (ghost button, square
   * corners, `text-2xs`) so it can sit flush inside a joined "interlinking"
   * control bar like {@link AgentSettingsChip}. Defaults to the standalone
   * outlined chip used everywhere else.
   */
  segment?: boolean;
  /**
   * Collapse the trigger to just the key's type-distinct icon (subscription
   * vs API key) instead of the full "provider · name" string. The full
   * details surface in a popover. Pairs naturally with `disabled` for a
   * read-only summary bar: the trigger stays clickable (to reveal the
   * details popover) even when key selection is disabled.
   */
  compact?: boolean;
  /**
   * Draw attention to the trigger (ring + pulse) — used when the surface
   * needs a key chosen before submission. Purely visual; does not block.
   */
  highlight?: boolean;
  /**
   * Whether the picker may emit a default selection through `onChange` on its
   * own (remembered pick, or a sole selectable candidate). Defaults to true,
   * which is what pre-submit forms want: the value lands in local form state
   * and is only committed when the user submits.
   *
   * Pass `false` when `onChange` is itself a write — e.g. re-pointing a
   * running agent's subscription, where an auto-emitted default would fire an
   * unsolicited mutation (and its error toast) just because the surface was
   * opened. With this off, the picker only reports explicit user picks and
   * leaves an unresolvable `value` alone.
   */
  autoSelectDefault?: boolean;
  /** DOM id on the trigger button, for pairing with an `InspectorField`'s `htmlFor`. */
  id?: string;
}

function readRemembered(storageKey?: string): string | null {
  if (!storageKey) return null;
  try {
    return localStorage.getItem(storageKey);
  } catch {
    return null;
  }
}

function writeRemembered(storageKey: string | undefined, id: string | null): void {
  if (!storageKey) return;
  try {
    if (id) localStorage.setItem(storageKey, id);
    else localStorage.removeItem(storageKey);
  } catch {
    // Ignore — storage unavailable (private browsing, quota, etc.)
  }
}

/**
 * Compact key picker for a model-provider secret (`mps_*`). Renders a
 * popover-backed trigger styled like the launch dialog's persona header,
 * filtered strictly to `provider` and `allowedTypes`. Each row carries an
 * `OwnerChip` so the operator sees whose key (theirs / a team's / the
 * org's) is about to be used. The last explicit pick is remembered in
 * `localStorage` and pre-selected on the next mount; with no remembered
 * pick, a sole selectable key is selected automatically.
 */
const TYPE_LABEL: Record<ModelProviderSecretType, string> = {
  apiKey: 'API key',
  subscription: 'Subscription',
};

// Compact per-row badge so users can tell an API key apart from a
// subscription (e.g. Claude Max) when several keys share a name. The
// subscription variant uses the same success-toned outline as the costs
// page so subscriptions read distinctly across surfaces.
const TYPE_BADGE_LABEL: Record<ModelProviderSecretType, string> = {
  apiKey: 'Key',
  subscription: 'Sub',
};

const TYPE_BADGE_VARIANT: Record<ModelProviderSecretType, 'secondary' | 'outline'> = {
  apiKey: 'secondary',
  subscription: 'outline',
};

const TYPE_BADGE_CLASS: Record<ModelProviderSecretType, string> = {
  apiKey: '',
  subscription: 'border-status-success/40 text-status-success',
};

export function ModelProviderSecretPicker({
  value,
  onChange,
  provider,
  allowedTypes,
  disabledTypes,
  storageKey,
  onEmptyAddHref,
  allowCreate = false,
  addSubscription,
  refreshSelected,
  manageSubscription,
  disabled,
  owner,
  className,
  ghost = false,
  segment = false,
  compact = false,
  highlight = false,
  autoSelectDefault = true,
  id,
}: ModelProviderSecretPickerProps) {
  const createProvider = PersistableModelProviderSchema.safeParse(provider).data;
  const canCreate = allowCreate && createProvider !== undefined;
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'select' | 'createKey'>('select');
  // Register with any surrounding chip bar so opening the key picker suppresses
  // sibling chips' hover previews (the picker itself has no hover card).
  useHoverPreviewGroup(open);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Radix Dialog wraps content in `react-remove-scroll`; popovers portaled
  // to document.body fall outside that shard and their inner scroll locks.
  // Portal into the dialog content when we're inside one so the cmdk list
  // scrolls. Harmless (null) outside a dialog.
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  useEffect(() => {
    const host = wrapperRef.current?.closest('[data-slot="dialog-content"]');
    setPortalContainer((host as HTMLElement | null) ?? null);
  }, []);

  const { secrets, isLoading, isFetching, isError, refetch } = useAccessibleModelProviderSecrets({
    types: allowedTypes,
    owner,
  });

  const candidates = useMemo(
    () => secrets.filter((s) => s.provider === provider),
    [secrets, provider],
  );

  const isTypeDisabled = useMemo(() => {
    const set = new Set(disabledTypes ?? []);
    return (type: ModelProviderSecret['type']) => set.has(type);
  }, [disabledTypes]);

  // Selectable candidates exclude rows whose type is disabled for this
  // provider (e.g. subscription keys when only API keys are supported).
  const selectableCandidates = useMemo(
    () => candidates.filter((c) => !isTypeDisabled(c.type)),
    [candidates, isTypeDisabled],
  );

  // Rows that may carry the update cog. Only the signed-in user's own
  // subscriptions can be updated: the credential behind a mirror row is
  // user-tier, so a team/org row (or an API key) has nothing the caller's
  // dialog could re-paste. Scope is a sufficient test for "mine" because the
  // accessible-secrets endpoint only ever returns the caller's own user-tier
  // rows, whoever the `owner` filter names.
  const canManageSecret = useMemo(
    () => (secret: ModelProviderSecret) =>
      manageSubscription !== undefined &&
      secret.type === 'subscription' &&
      secret.scope === 'user' &&
      !isTypeDisabled(secret.type),
    [manageSubscription, isTypeDisabled],
  );
  const hasManageableRow = useMemo(
    () => candidates.some(canManageSecret),
    [candidates, canManageSecret],
  );

  // Tracks the last value we pushed through `onChange` so the reconcile
  // effect emits a given default at most once. Without this, a parent whose
  // controlled `value` updates asynchronously (e.g. a server mutation
  // round-trip) leaves `value` stale across renders; combined with an
  // unstable `onChange` identity the effect would re-run and re-emit the same
  // default every render, looping until the round-trip settles and tripping
  // React's max-update-depth guard.
  const lastEmittedRef = useRef<string | null | undefined>(undefined);

  // Keep the selection valid and apply a default pick. Runs whenever
  // the candidate list settles (provider change, key added/removed):
  //   - a still-valid, selectable value is kept untouched,
  //   - an invalid/empty/now-disabled value falls back to the remembered pick
  //     for this provider (the `storageKey` is provider-scoped),
  //   - with no usable remembered pick, a sole selectable candidate is
  //     auto-selected (first-launch: the one key added during setup),
  //   - otherwise clears. The auto-pick is ephemeral — only explicit picks
  //     are written to `localStorage`, so it never shadows a later choice.
  // Writing through `onChange` (a parent callback) keeps the component
  // controlled; mirrors the effect-driven default in `AuthMethodSelector`.
  // Skipped entirely when `autoSelectDefault` is false — surfaces whose
  // `onChange` is a write opt out so merely rendering never mutates.
  useEffect(() => {
    if (!autoSelectDefault) return;
    // A refetch in flight means the list in hand is knowably stale — e.g. a key
    // was just created elsewhere and invalidated. Reconciling against it would
    // discard a `value` the parent set from that creation, so wait for the
    // fresh list. `isLoading` alone misses this: it is false while
    // refetching over existing data.
    if (isLoading || isFetching) return;
    // Don't wipe a valid selection just because the list failed to load —
    // the fetch may recover on retry. Only reconcile against a list we
    // actually fetched.
    if (isError) return;
    if (value && selectableCandidates.some((c) => c.id === value)) {
      // A valid value means the parent accepted our pick (or set its own);
      // clear the latch so a future provider change can default again.
      lastEmittedRef.current = undefined;
      return;
    }
    const remembered = readRemembered(storageKey);
    const next =
      remembered && selectableCandidates.some((c) => c.id === remembered)
        ? remembered
        : selectableCandidates.length === 1
          ? selectableCandidates[0].id
          : null;
    if (next === value) return;
    // Already emitted this exact default and the parent hasn't reflected it
    // back into `value` yet — don't spam `onChange` while we wait.
    if (next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    onChange(next);
  }, [
    autoSelectDefault,
    isLoading,
    isFetching,
    isError,
    value,
    selectableCandidates,
    storageKey,
    onChange,
  ]);

  const selected: ModelProviderSecret | null = useMemo(
    () => candidates.find((c) => c.id === value) ?? null,
    [candidates, value],
  );

  const grouped = useMemo(() => {
    const byScope = new Map<ModelProviderSecretScope, ModelProviderSecret[]>();
    for (const secret of candidates) {
      const list = byScope.get(secret.scope) ?? [];
      list.push(secret);
      byScope.set(secret.scope, list);
    }
    return SCOPE_GROUP_ORDER.map((scope) => ({ scope, items: byScope.get(scope) ?? [] })).filter(
      (g) => g.items.length > 0,
    );
  }, [candidates]);

  function handleSelect(id: string) {
    const candidate = candidates.find((c) => c.id === id);
    if (candidate && isTypeDisabled(candidate.type)) return;
    onChange(id);
    writeRemembered(storageKey, id);
    setOpen(false);
  }

  // Switch the popover body into the inline create form. The popover
  // stays open so focus and stacking aren't disrupted — the form's
  // Cancel button (or success) flips us back to 'select'.
  function handleAddKey() {
    if (!canCreate) return;
    setMode('createKey');
  }

  function handleCreated(secret: ModelProviderSecret) {
    onChange(secret.id);
    writeRemembered(storageKey, secret.id);
    // Drop back to the select view so the user sees the new key in
    // context (with its scope group + chip) before deciding whether to
    // confirm by closing the popover themselves.
    setMode('select');
  }

  // Close the popover before invoking the subscription handler so the
  // launched dialog doesn't fight the picker for focus / stacking. Routed
  // through `handleOpenChange` because this also fires from the create-key
  // view — closing without resetting `mode` would reopen the picker on a
  // half-filled form instead of the key list.
  function handleAddSubscription() {
    if (!addSubscription) return;
    handleOpenChange(false);
    addSubscription.onAdd();
  }

  // Same close-first reasoning as `handleAddSubscription`: the dialog the
  // caller opens would otherwise fight the popover for focus and stacking.
  function handleManageSubscription(secret: ModelProviderSecret) {
    if (!manageSubscription) return;
    handleOpenChange(false);
    manageSubscription.onManage(secret);
  }

  // Reset the popover view whenever it closes so reopening always starts
  // on the select list rather than a half-filled create form.
  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setMode('select');
  }

  const providerLabel = MODEL_PROVIDER_LABELS[provider];
  const showSubscriptionAffordance =
    addSubscription !== undefined &&
    allowedTypes.includes('subscription') &&
    !isTypeDisabled('subscription');
  // Whether the popover offers its own "add credential" rows. When it does,
  // the empty state stays purely informational — the rows below it are the
  // affordance, so `onEmptyAddHref` would only duplicate them.
  const hasInlineActions = canCreate || showSubscriptionAffordance;

  // A disabled picker is read-only. In the default (full) form that simply
  // locks the trigger. In compact form the popover becomes a key-details
  // view, so the trigger must stay clickable to reveal it — only the
  // non-compact, disabled trigger is truly inert.
  const readOnly = Boolean(disabled);
  const triggerDisabled = Boolean(disabled) && !compact;

  return (
    <div ref={wrapperRef} className="flex shrink-0 items-center">
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            id={id}
            variant={segment || ghost ? 'ghost' : 'outline'}
            size="sm"
            role="combobox"
            aria-expanded={open}
            aria-label={readOnly ? 'Model provider key details' : 'Select model provider key'}
            disabled={triggerDisabled}
            className={cn(
              'h-7 justify-start gap-1.5 px-2 font-normal',
              segment ? 'rounded-none text-2xs focus-visible:ring-inset' : 'max-w-60 text-xs',
              compact && 'w-9 justify-center gap-0 px-0',
              highlight &&
                'ring-2 ring-destructive ring-offset-1 ring-offset-background animate-pulse',
              className,
            )}
          >
            <ModelProviderSecretChip secret={selected} placeholder="Choose key" compact={compact} />
            {compact ? null : (
              <ChevronDown className="ml-auto h-3.5 w-3.5 shrink-0 text-muted-foreground/70" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            'overflow-y-auto p-0',
            mode === 'createKey' || readOnly
              ? 'w-80'
              : // The cap grows when rows carry a cog: a selected subscription
                // row then holds a badge, "Selected", a refresh button and the
                // cog, which at 18rem clipped the name to a few characters.
                cn('w-auto min-w-[12rem]', hasManageableRow ? 'max-w-[21rem]' : 'max-w-[18rem]'),
          )}
          align="start"
          data-tutorial-id="provider-key-menu"
          // Swapping into the inline "Add a key" form unmounts the focused
          // command item; focus falls to <body> and — when portaled into a
          // Dialog — the dialog's focus scope pulls it back to a focusable
          // outside this layer, which Radix reads as a focus-outside and would
          // use to dismiss the picker mid-flow. Suppress that path; genuine
          // outside clicks (pointer) and Escape still close the popover.
          onFocusOutside={(event) => event.preventDefault()}
          container={portalContainer ?? undefined}
          // When portaled into the (overflow-hidden) dialog, constrain
          // collision detection to that container so the popover flips above
          // the trigger instead of opening downward and being clipped by the
          // dialog's bottom edge / footer.
          collisionBoundary={portalContainer ?? undefined}
          collisionPadding={8}
          // Never exceed the dialog's available height: cap to the space the
          // collision boundary allows and scroll internally so the popover is
          // never clipped by the modal rim.
          style={{ maxHeight: 'var(--radix-popover-content-available-height)' }}
        >
          {readOnly ? (
            <div className="p-3" data-testid="mps-picker-details">
              {selected ? (
                <ModelProviderSecretPreviewCard secret={selected} />
              ) : (
                <p className="text-xs text-muted-foreground">
                  {isLoading ? 'Loading key…' : 'No key selected.'}
                </p>
              )}
            </div>
          ) : mode === 'createKey' && createProvider ? (
            <div className="p-3" data-testid="mps-picker-create-form">
              <p className="mb-3 text-xs font-medium">Add {providerLabel} key</p>
              <AddModelProviderKeyForm
                provider={createProvider}
                onCreated={handleCreated}
                onCancel={() => setMode('select')}
              />
              {/*
               * The form only takes a pasted API key. A subscription holder
               * who landed here from the empty state would otherwise have to
               * back out to find the registration flow, so keep it one click
               * away.
               */}
              {showSubscriptionAffordance ? (
                <div className="mt-3 border-t border-border pt-3 text-center">
                  <button
                    type="button"
                    onClick={handleAddSubscription}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    data-testid="mps-picker-create-add-subscription"
                  >
                    <Sparkles className="h-3 w-3" />
                    {addSubscription.label}
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <Command>
              <CommandInput placeholder={`Search ${providerLabel} keys…`} className="h-9" />
              <CommandList className="max-h-72">
                <CommandEmpty>
                  <div className="space-y-2 px-2 py-3 text-center">
                    {isError ? (
                      // Distinguish a failed fetch from a genuinely empty list —
                      // showing "No keys available" on an error wrongly implies
                      // the user has none and hides the recoverable failure.
                      <>
                        <p className="text-sm text-destructive">
                          Couldn’t load {providerLabel} keys.
                        </p>
                        <button
                          type="button"
                          onClick={() => void refetch()}
                          className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                        >
                          <RefreshCw className="h-3 w-3" />
                          Retry
                        </button>
                      </>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          {isLoading ? 'Loading keys…' : `No ${providerLabel} keys available.`}
                        </p>
                        {!isLoading && !hasInlineActions && onEmptyAddHref ? (
                          <a
                            href={onEmptyAddHref}
                            className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                          >
                            <Plus className="h-3 w-3" />
                            Add a key
                          </a>
                        ) : null}
                      </>
                    )}
                  </div>
                </CommandEmpty>
                {grouped.map((group) => (
                  <CommandGroup key={group.scope} heading={SCOPE_GROUP_LABEL[group.scope]}>
                    {group.items.map((secret) => {
                      const typeDisabled = isTypeDisabled(secret.type);
                      const displayName = modelProviderSecretDisplayName(secret);
                      const isSelected = secret.id === value;
                      return (
                        <CommandItem
                          key={secret.id}
                          value={`${displayName} ${secret.id}`}
                          onSelect={() => handleSelect(secret.id)}
                          disabled={typeDisabled}
                          data-active={isSelected ? 'true' : undefined}
                          data-testid={`mps-option-${secret.id}`}
                          className={cn(
                            'gap-2',
                            isSelected && 'bg-primary/10 data-[selected=true]:bg-primary/15',
                          )}
                        >
                          <OwnerChip ownerId={secret.ownerId} />
                          <span
                            className={cn(
                              'flex-1 truncate',
                              isSelected && 'font-medium text-primary',
                            )}
                          >
                            {displayName}
                          </span>
                          <Badge
                            variant={TYPE_BADGE_VARIANT[secret.type]}
                            className={cn(
                              'shrink-0 px-1 py-0 text-2xs',
                              TYPE_BADGE_CLASS[secret.type],
                            )}
                            data-testid={`mps-option-type-${secret.id}`}
                          >
                            {TYPE_BADGE_LABEL[secret.type]}
                          </Badge>
                          {typeDisabled ? (
                            <span className="text-2xs text-muted-foreground">
                              {TYPE_LABEL[secret.type]} not supported
                            </span>
                          ) : isSelected ? (
                            <span className="inline-flex shrink-0 items-center gap-1 text-2xs font-medium text-primary">
                              <Check className="h-3.5 w-3.5" />
                              Selected
                            </span>
                          ) : null}
                          {isSelected && !typeDisabled && refreshSelected ? (
                            // Nested in the row so the control sits with the
                            // credential it re-applies. cmdk turns a click on
                            // the row into `onSelect`, which would close the
                            // popover and hide the pending state, so both
                            // pointer events stop here.
                            <button
                              type="button"
                              aria-label={refreshSelected.label}
                              title={refreshSelected.label}
                              disabled={refreshSelected.pending}
                              data-testid={`mps-option-refresh-${secret.id}`}
                              onPointerDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                event.stopPropagation();
                                refreshSelected.onRefresh();
                              }}
                              className="inline-flex shrink-0 items-center rounded p-0.5 text-primary hover:bg-primary/10 disabled:pointer-events-none disabled:opacity-60"
                            >
                              <RefreshCw
                                className={cn(
                                  'h-3.5 w-3.5',
                                  refreshSelected.pending && 'animate-spin',
                                )}
                              />
                            </button>
                          ) : null}
                          {manageSubscription && canManageSecret(secret) ? (
                            // Same nesting (and pointer-event trapping) as the
                            // refresh control: a click here must open the
                            // caller's dialog, not pick the row.
                            <button
                              type="button"
                              aria-label={`${manageSubscription.label}: ${displayName}`}
                              title={manageSubscription.label}
                              data-testid={`mps-option-manage-${secret.id}`}
                              onPointerDown={(event) => event.stopPropagation()}
                              onClick={(event) => {
                                event.stopPropagation();
                                handleManageSubscription(secret);
                              }}
                              className="inline-flex shrink-0 items-center rounded p-0.5 text-muted-foreground hover:bg-primary/10 hover:text-primary"
                            >
                              <Settings className="h-3.5 w-3.5" />
                            </button>
                          ) : null}
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                ))}
                {/*
                 * Always mounted — a user with zero keys is exactly who needs
                 * these rows, and gating them on a non-empty list left the
                 * empty state offering only an API-key form (no way to
                 * register a subscription at all).
                 *
                 * `forceMount` on the group and its rows keeps them out of
                 * cmdk's filter bookkeeping: they stay actionable when a
                 * search query hides every key row, and — because unregistered
                 * items don't count toward `filtered.count` — the empty state
                 * above still renders when there are no keys to list.
                 */}
                {hasInlineActions ? (
                  <>
                    {candidates.length > 0 ? <CommandSeparator /> : null}
                    <CommandGroup forceMount>
                      {canCreate ? (
                        <CommandItem
                          forceMount
                          value="__add_model_provider_key__"
                          onSelect={handleAddKey}
                          className="gap-2 text-primary"
                          data-testid="mps-picker-add-key"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          {/* No article: most provider labels start with a
                              vowel sound ("Anthropic", "OpenAI", "xAI"), and
                              this matches the create view's own heading. */}
                          <span>Add {providerLabel} key</span>
                        </CommandItem>
                      ) : null}
                      {showSubscriptionAffordance ? (
                        <CommandItem
                          forceMount
                          value="__add_model_provider_subscription__"
                          onSelect={handleAddSubscription}
                          className="gap-2 text-primary"
                          data-testid="mps-picker-add-subscription"
                        >
                          <Sparkles className="h-3.5 w-3.5" />
                          <span>{addSubscription.label}</span>
                        </CommandItem>
                      ) : null}
                    </CommandGroup>
                  </>
                ) : null}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
